import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, InputRef, Table } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'
import { path } from 'src/libs'
import { Guard } from 'src/features/session/guard'
import { getColumnSearchProps } from 'src/libs/tableFunctions'
import { TasksTypesNames } from 'src/pages/goldenMay2026/constants'
import {
  ChangeTaskModal,
  ClientTaskModal,
} from 'src/pages/goldenMay2026/tasks/widgets'
import { useTasksList, useTasksTypesList } from '../hooks'
import { onChangeTaskList, taskTypeListLoad } from '../model'
import { Task } from '../types'

export const TasksList = () => {
  const [isChangeTaskModalOpen, setIsChangeTaskModalOpen] = useState(false)
  const [isClientTasksModalOpen, setIsClientTasksModalOpen] = useState<{
    data: Task
  } | null>(null)
  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const searchInput = useRef<InputRef>(null)

  const { tasks, loading, totalCountOfTasks } = useTasksList()
  const { tasksTypes } = useTasksTypesList()

  useEffect(() => {
    taskTypeListLoad()
  }, [])

  useEffect(() => {
    onChangeTaskList({
      searchText,
      page: 1,
    })
  }, [searchText])

  const taskTypeFilters = useMemo(
    () =>
      tasksTypes.map((item) => ({
        value: item.taskType,
        text: TasksTypesNames[item.taskType as keyof typeof TasksTypesNames],
      })),
    [tasksTypes],
  )

  const columns: ColumnsType<Task> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...getColumnSearchProps<Task>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Task>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Task>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Task',
      dataIndex: 'taskType',
      render: (item: string) =>
        TasksTypesNames[item as keyof typeof TasksTypesNames],
      filters: taskTypeFilters,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: [
        {
          text: 'new',
          value: 'check',
        },
        {
          text: 'rejected',
          value: 'declined',
        },
        {
          text: 'accepted',
          value: 'accepted',
        },
      ],
      filterMultiple: false,
      defaultFilteredValue: ['check'],
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'center',
      render: (item: Task) => {
        return (
          <Guard permission="golden_may_2026_task_update">
            <Button
              onClick={() => {
                setIsClientTasksModalOpen({ data: item })
              }}
            >
              <SearchOutlined style={{ fontSize: 16 }} />
            </Button>
          </Guard>
        )
      },
    },
  ]

  return (
    <>
      <Guard permission="golden_may_2026_task_edit">
        <Button
          color="orange"
          size="large"
          variant="solid"
          onClick={() => setIsChangeTaskModalOpen(true)}
          style={{ alignSelf: 'flex-start', color: 'black', marginBottom: 24 }}
        >
          Change tasks
        </Button>
      </Guard>
      <Table
        dataSource={tasks}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey='id'
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTasks,
        }}
        onChange={(pagination, filters, sorter: any) =>
          onChangeTaskList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }
      />
      <ChangeTaskModal
        isVisible={isChangeTaskModalOpen}
        onClose={() => setIsChangeTaskModalOpen(false)}
      />
      {isClientTasksModalOpen && (
        <ClientTaskModal
          isVisible={Boolean(isClientTasksModalOpen)}
          onClose={() => setIsClientTasksModalOpen(null)}
          data={isClientTasksModalOpen.data}
        />
      )}
    </>
  )
}
